import React, { useState } from 'react';
import { ArrowRightLeft, Shield, FileText, CheckCircle, Clock, Target, Award, ChevronDown, ChevronUp, Users, Briefcase } from 'lucide-react';
import RegistrationModal from '../../components/RegistrationModal';
import SEO from '../../components/SEO';
import ServicePricing from '../../components/ServicePricing';
import CTASection from '../../components/CTASection';

const TrademarkTransferPage = () => {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [modalMode, setModalMode] = useState(null);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const openModal = (mode) => {
    setModalMode(mode);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setModalMode(null);
  };

  const benefits = [
    {
      icon: Shield,
      title: "Secure Transfer",
      description: "Legally compliant trademark transfers that protect both buyer and seller interests."
    },
    {
      icon: FileText,
      title: "Complete Documentation",
      description: "All required assignments, declarations, and supporting documents prepared professionally."
    },
    {
      icon: Target,
      title: "USPTO Recording",
      description: "Official recording with USPTO to establish clear chain of title and public notice."
    },
    {
      icon: CheckCircle,
      title: "Title Clearance",
      description: "Verification of ownership and identification of potential title issues before transfer."
    },
    {
      icon: Users,
      title: "Multi-Party Coordination",
      description: "Managing complex transfers involving multiple parties, marks, or transaction structures."
    },
    {
      icon: Award,
      title: "Business Integration",
      description: "Strategic advice on trademark transfers in mergers, acquisitions, and business sales."
    }
  ];

  const transferTypes = [
    {
      title: "Assignment (Sale)",
      description: "Complete transfer of trademark ownership",
      features: ["Permanent ownership change", "Full rights transfer", "Goodwill inclusion", "Consideration payment"]
    },
    {
      title: "Merger/Acquisition",
      description: "Trademark transfer in business combination",
      features: ["Entity consolidation", "Portfolio transfer", "Due diligence support", "Integration planning"]
    },
    {
      title: "Corporate Restructuring",
      description: "Transfer between related entities",
      features: ["Parent-subsidiary transfers", "Sister company assignments", "Holding company structures", "Tax planning support"]
    },
    {
      title: "Security Interest",
      description: "Trademark as collateral for financing",
      features: ["Loan collateral", "Security agreements", "UCC filings", "Foreclosure procedures"]
    }
  ];

  const process = [
    {
      step: "1",
      title: "Title Review",
      description: "Comprehensive review of trademark ownership records, existing agreements, and any encumbrances or liens.",
      timeline: "2-3 days"
    },
    {
      step: "2",
      title: "Agreement & Execution",
      description: "Draft, review, and execute all transfer documents including assignment agreements with proper notarization.",
      timeline: "3-5 days"
    },
    {
      step: "3",
      title: "USPTO Recording",
      description: "File the assignment with the USPTO, update all records, and provide you with official confirmation of transfer.",
      timeline: "1-3 months"
    }
  ];

  const packages = [
    {
      name: "Basic Transfer",
      price: "$499",
      description: "Single trademark assignment",
      features: [
        "1 trademark transfer",
        "Assignment agreement",
        "USPTO recording",
        "Title verification",
        "Standard processing",
        "Email support"
      ],
      popular: false
    },
    {
      name: "Business Transfer",
      price: "$999",
      description: "Multiple marks or complex transfers",
      features: [
        "Up to 5 trademarks",
        "Custom agreements",
        "Goodwill documentation",
        "Chain of title verification",
        "Priority processing",
        "Phone support",
        "Post-transfer assistance"
      ],
      popular: true
    },
    {
      name: "M&A Transfer",
      price: "$2,499",
      description: "Portfolio transfer with due diligence",
      features: [
        "Unlimited trademarks",
        "Complete due diligence",
        "Portfolio valuation",
        "Risk assessment",
        "Transaction coordination",
        "Dedicated attorney",
        "Integration support"
      ],
      popular: false
    }
  ];

  const faqs = [
    {
      question: "What is a trademark transfer and when is it necessary?",
      answer: "Trademark transfer (assignment) is the legal transfer of trademark ownership from one party (assignor) to another (assignee). It's necessary when: (1) selling a business or brand, (2) mergers and acquisitions, (3) corporate restructuring, (4) transferring marks between related entities, (5) securing financing with trademarks as collateral, or (6) gift or inheritance situations. Proper transfer requires written assignment agreements and USPTO recording to establish clear ownership and maintain trademark validity."
    },
    {
      question: "Do I need to record trademark transfers with the USPTO?",
      answer: "While not legally required, recording trademark assignments with the USPTO is strongly recommended because it: (1) provides constructive notice to third parties, (2) establishes priority over subsequent transferees, (3) creates public record of ownership, (4) enables enforcement actions by new owner, (5) satisfies requirements for renewal and maintenance, and (6) protects against claims by previous owners. Unrecorded assignments can create title disputes, complicate enforcement, and jeopardize trademark rights. Recording fee is $100 per mark."
    },
    {
      question: "Can I transfer a trademark application or only registered marks?",
      answer: "Both trademark applications and registrations can be transferred. Application transfers are common during business sales or reorganizations before registration completes. However, application transfers require: (1) written assignment, (2) USPTO recording to update owner records, and (3) new owner must continue prosecution. Intent-to-use applications require assignor to transfer goodwill associated with the mark. Transfers don't affect application processing timeline or examination, but new owner receives all communications and controls prosecution."
    },
    {
      question: "What is 'goodwill' and why must it be transferred with trademarks?",
      answer: "Goodwill is the business reputation and customer recognition associated with a trademark. Trademark law requires goodwill transfer with the mark (except between related entities) because trademarks identify source—transferring the mark without its associated business context can deceive consumers. Assignment agreements must explicitly state goodwill is being transferred. 'Naked assignments' (without goodwill) can invalidate trademarks. For business sales, goodwill includes customer lists, business records, and going concern value."
    },
    {
      question: "How long does it take to complete a trademark transfer?",
      answer: "Timeline varies by complexity. Simple assignment: 4-8 weeks total (1-2 weeks preparation/execution, 3-6 weeks USPTO processing). Complex transfers: 2-4 months (due diligence, negotiations, multiple marks). USPTO recording typically takes 3-6 months for official acknowledgment, though ownership transfers immediately upon proper assignment execution. Expedited processing available for urgent situations. Business acquisition transfers often coordinate with broader transaction timelines."
    },
    {
      question: "What documents are required for trademark transfer?",
      answer: "Required documents include: (1) Trademark Assignment Agreement (signed by assignor and assignee), (2) Recordation Cover Sheet (USPTO form), (3) Proof of execution (notarization or declaration), (4) Payment of USPTO recordation fee ($100 per mark). Additional documents may include: merger certificates, corporate resolutions, chain of title documentation, goodwill transfer documents, or consent agreements. We prepare all required documents ensuring legal compliance and USPTO acceptance."
    },
    {
      question: "Can I transfer some but not all trademarks in a portfolio?",
      answer: "Yes, you can transfer individual marks or groups of marks without transferring your entire portfolio. This is common when: (1) selling specific product lines, (2) spinning off business divisions, (3) licensing arrangements convert to assignments, or (4) settling disputes. Each mark requires separate assignment and recordation. However, transferring related marks together is advisable to maintain brand family integrity and avoid consumer confusion. We help structure partial transfers to optimize business objectives while managing legal risks."
    },
    {
      question: "What happens to pending trademark applications during business acquisition?",
      answer: "Pending applications must be assigned to the acquiring entity to maintain rights. Process involves: (1) executing assignment agreements covering applications, (2) recording assignments with USPTO, (3) updating owner information in TEAS, (4) ensuring new owner continues prosecution. The acquiring entity becomes responsible for: responding to office actions, filing Statements of Use, and paying maintenance fees. Failure to properly transfer applications can result in abandonment. We coordinate application transfers as part of acquisition closing to ensure continuity."
    },
    {
      question: "How much does trademark transfer service cost?",
      answer: "Our fees: Basic single trademark transfer: $499. Business transfer (up to 5 marks): $999. M&A portfolio transfer: $2,499+. USPTO recording fee: $100 per trademark (paid directly to USPTO). Total cost depends on: number of marks, transaction complexity, due diligence scope, and urgency. Complex transactions involving multiple parties, international marks, or disputes cost more. Investment protects against title defects, ensures legal compliance, and establishes clear ownership—critical for trademark value and enforcement."
    },
    {
      question: "What are common problems with trademark transfers?",
      answer: "Common issues include: (1) incomplete chain of title (missing prior assignments), (2) assignments without goodwill (can invalidate mark), (3) unauthorized signatures (corporate authority issues), (4) failure to record with USPTO (creates title ambiguity), (5) conflicting claims from multiple parties, (6) liens or encumbrances on marks, (7) assignment of abandoned applications, (8) trademark not owned by claimed assignor. These problems can delay closings, reduce business value, prevent enforcement, or result in ownership disputes. Professional transfer services identify and resolve issues before they become costly problems."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEO 
        title="Trademark Transfer & Assignment Services - Change of Ownership"
        description="Trademark transfer and assignment for business sales, mergers, and ownership changes. Expert USPTO recording."
        keywords="trademark transfer, trademark assignment, change of ownership, trademark sale, business acquisition"
      />

      {/* Hero Section */}
      <section className="relative text-white py-20 overflow-hidden">
            <img src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=900w=1600&q=80q=50" alt="" loading="eager" fetchpriority="high" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#2F1A8E]/90 via-[#1a0f5e]/85 to-[#0d0830]/90"></div>
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF035B]/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#FF035B]/5 rounded-full blur-2xl"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#FF035B]/20 text-[#FF035B] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <ArrowRightLeft className="w-4 h-4" />
                <span>Trademark Transfer Services</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Professional Trademark Transfer & Assignment Services
              </h1>
              <p className="text-xl mb-8 text-gray-300">
                Secure trademark transfers for business sales, mergers, acquisitions, and ownership changes. Complete documentation, USPTO recording, and legal compliance.
              </p>
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={() => openModal('registration')}
                  className="bg-[#FF035B] hover:bg-[#2F1A8E] text-white px-8 py-4 rounded-lg font-semibold transition-all hover:shadow-xl hover:scale-105"
                >
                  Start Transfer Process
                </button>
                <button 
                  onClick={() => { if (window.cd360Api && window.cd360Api.liveChat) window.cd360Api.liveChat.showWebWidget(); }}
                  className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all"
                >
                  Speak with Attorney
                </button>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Secure Transfer</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>USPTO Recording</span>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
              <h3 className="text-lg font-bold mb-6">Why Proper Transfer Matters</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Clear Ownership</strong>
                    <span className="text-gray-300">Establish unambiguous trademark ownership for enforcement and business value</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Public Notice</strong>
                    <span className="text-gray-300">USPTO recording provides constructive notice to all third parties</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Avoid Disputes</strong>
                    <span className="text-gray-300">Proper documentation prevents ownership challenges and title defects</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Professional Transfer Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive trademark transfer support for any transaction
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="group bg-white hover:bg-gradient-to-br hover:from-[#FF035B] hover:to-[#2F1A8E] rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:scale-105 cursor-pointer hover:shadow-xl transition-all border border-gray-100">
                <benefit.icon className="w-12 h-12 text-[#FF035B] group-hover:text-white transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-white transition-colors duration-500 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 group-hover:text-white/90 transition-colors duration-500 leading-relaxed group-hover:text-white/90 transition-colors duration-500">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Transfer Types */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Types of Trademark Transfers
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We handle all trademark transfer scenarios
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {transferTypes.map((type, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all">
                <h3 className="text-lg font-bold text-gray-900 group-hover:text-white transition-colors duration-500 mb-3">{type.title}</h3>
                <p className="text-gray-600 group-hover:text-white/90 transition-colors duration-500 mb-6">{type.description}</p>
                <div className="space-y-2">
                  {type.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-gray-700">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Transfer Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From title review to USPTO recording
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-gradient-to-br from-[#FF035B] to-[#2F1A8E] text-white rounded-xl p-6 shadow-lg">
                  <div className="text-4xl font-bold mb-2 opacity-50">{item.step}</div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-sm mb-4 text-white/90">{item.description}</p>
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Clock className="w-4 h-4" />
                    {item.timeline}
                  </div>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-[#FF035B] to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <ServicePricing showPrices={true} title="Transfer Packages" subtitle="Professional transfer services for any situation" />


      {/* FAQ */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about trademark transfers
            </p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-all"
                >
                  <span className="font-semibold text-gray-900 pr-8">{faq.question}</span>
                  {openFAQ === index ? (
                    <ChevronUp className="w-5 h-5 text-[#FF035B] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openFAQ === index && (
                  <div className="px-8 pb-6">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Transfer Your Trademarks?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Protect your business investment with professional trademark transfer services. Clear title, proper documentation, and USPTO recording.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button 
              onClick={() => openModal('registration')}
              className="bg-white text-[#FF035B] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all hover:shadow-xl"
            >
              Start Transfer Process
            </button>
            <button 
              onClick={() => { if (window.cd360Api && window.cd360Api.liveChat) window.cd360Api.liveChat.showWebWidget(); }}
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#FF035B] transition-all"
            >
              Speak with Attorney
            </button>
          </div>
        </div>
      </section>

      <CTASection />

      <RegistrationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialType="registration"
      />
    </div>
  );
};


export default TrademarkTransferPage;
